import { forwardRef } from "react";
import type { LucideIcon } from "lucide-react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { HoverLabel } from "@/components/ui/hover-label";
import { cn } from "@/lib/utils";

export interface IconButtonProps extends Omit<ButtonProps, "children" | "size" | "title"> {
  icon: LucideIcon;
  /** REQUIRED. The button's name: announced by screen readers and shown on hover. Describe the action, not the icon. */
  ariaLabel: string;
  /** Optional one-line explanation shown under the name on hover. */
  hint?: string;
  /** Why the button is greyed out. Shown under the name, even with help tips off, only while disabled. */
  disabledReason?: string | null;
  /** Which side the hover label opens on. Default "top". */
  hintSide?: "top" | "right" | "bottom" | "left";
  /** Visual size. Default "icon" (40x40 from button variants). */
  size?: "icon" | "sm" | "lg";
  iconClassName?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon: Icon, ariaLabel, hint, disabledReason, hintSide, size = "icon", iconClassName, className, ...props }, ref) => {
    // HoverLabel wraps the button while it is disabled, so the name and any
    // reason still show, and shows the reason only while it is disabled.
    return (
      <HoverLabel label={ariaLabel} hint={hint} reason={disabledReason ?? undefined} side={hintSide}>
        <Button
          ref={ref}
          size={size}
          aria-label={ariaLabel}
          className={className}
          {...props}
        >
          <Icon className={cn("h-4 w-4", iconClassName)} aria-hidden />
        </Button>
      </HoverLabel>
    );
  },
);
IconButton.displayName = "IconButton";
